import { useState } from 'react';
import {
	Box,
	Typography,
	TextField,
	Paper,
	Stack,
	Alert,
	Button,
	Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Actions from '../../components/Actions';
import { Navigate, useOutletContext, useRevalidator } from 'react-router';
import HandymanIcon from '@mui/icons-material/Handyman';
import CheckIcon from '@mui/icons-material/Check';
import { authedApiFetch } from '../../utils/Api';

const TenantAdmin = () => {
	// React Router hooks
	const { tenant, user } = useOutletContext();
	const revalidator = useRevalidator();

	const [salesforceOrgId, setSalesforceOrgId] = useState(
		tenant.salesforce_org_id || ''
	);

	const [orgIdLoading, setOrgIdLoading] = useState(false);
	const [orgIdError, setOrgIdError] = useState(null);
	const [isEditingOrgId, setIsEditingOrgId] = useState(false);

	const handleUpdateOrgId = async (e) => {
		e.preventDefault();
		setOrgIdLoading(true);
		setOrgIdError(null);

		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenant.id}`,
				method: 'PATCH',
				payload: {
					tenant: {
						salesforce_org_id: salesforceOrgId,
					},
				},
			});
			revalidator.revalidate();
			setIsEditingOrgId(false);
		} catch (err) {
			setOrgIdError(err.message);
		} finally {
			setOrgIdLoading(false);
		}
	};

	// handler passed to action component, called after confirming action
	const handleCreateUserPool = async () => {
		await authedApiFetch({
			endpoint: `/tenants/${tenant.id}/user_pool`,
			method: 'POST',
		});

		// imperatively revalidate b/c not using React Router action.
		revalidator.revalidate();
	};

	// Array of actions passed to Action component
	const actions = [
		{
			name: 'createUserPool',
			label: 'Create User Pool and App Clients',
			icon: <HandymanIcon />,
			disabledIcon: <CheckIcon />,
			confirmText:
				'This will create a user pool and app clients for the current tenant.',
			disabledCheck: () => !!tenant.user_pool_id,
			actionHandler: handleCreateUserPool,
		},
	];

	// Route back to marketplace if non-admin user attempts to navigate directly
	if (user.role != 'altvia_admin' && user.role != 'altvia_power_user') {
		return <Navigate to={`/tenants/${tenant.id}/marketplace`} />;
	} else {
		return (
			<>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						rowGap: '16px',
						height: '100vh',
						paddingTop: '12px',
						overflowY: 'auto',
						pb: 4,
					}}
				>
					<Typography variant='h4'>Tenant Admin</Typography>
					<Actions actions={actions} />

					{user.role === 'altvia_admin' && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Paper sx={{ p: 3, mt: 4, maxWidth: '800px' }}>
								<Box>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											mb: 2,
										}}
									>
										<Typography variant='h6' gutterBottom>
											AIM Request Integration
										</Typography>
										<Button
											variant={isEditingOrgId ? 'contained' : 'outlined'}
											color='error'
											size='small'
											onClick={() => setIsEditingOrgId(!isEditingOrgId)}
											disabled={orgIdLoading}
										>
											{isEditingOrgId ? 'Cancel' : 'Edit'}
										</Button>
									</Box>

									<Divider sx={{ my: 2 }} />
									<Typography
										variant='subtitle2'
										sx={{ fontWeight: 700, mb: 2 }}
									>
										15-character Salesforce Org ID
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
											color: 'text.secondary',
										}}
									>
										<Typography variant='body2'>
											AIM requests to Launchpad come with a Salesforce org ID.
										</Typography>

										<Typography variant='body2'>
											To ensure that AIM requests from this Tenant&apos;s
											Salesforce org are directed to it, make sure that it has
											the correct 15-character Salesforce ID.
										</Typography>

										<Box>
											<Typography variant='body2' sx={{ mb: 1 }}>
												The 15-character ID can be retrieved by following these
												steps:
											</Typography>
											<Box
												component='ol'
												sx={{
													m: 0,
													pl: 3,
													'& li': {
														fontSize: '0.875rem',
														mb: 0.5,
													},
												}}
											>
												<li>Log into your Salesforce org.</li>
												<li>Navigate to Setup and search for Company Information.</li>
												<li>In the Organization Detail section, you&apos;ll find the 15-character Salesforce Org ID in the <b>Salesforce.com Organization ID</b> field.</li>
											</Box>
										</Box>

										<form onSubmit={handleUpdateOrgId}>
											<Stack spacing={2}>
												<TextField
													label='15-character Salesforce Org ID'
													variant='outlined'
													size='small'
													value={salesforceOrgId}
													onChange={(e) => setSalesforceOrgId(e.target.value)}
													required
													disabled={orgIdLoading || !isEditingOrgId}
													error={
														salesforceOrgId.length > 0 &&
														salesforceOrgId.length !== 15
													}
													helperText={
														salesforceOrgId.length > 0 &&
														salesforceOrgId.length !== 15
															? `Must be exactly 15 characters (currently ${salesforceOrgId.length})`
															: ''
													}
													slotProps={{
														htmlInput: {
															maxLength: 15,
														},
													}}
												/>
												{orgIdError && (
													<Alert severity='error' size='small'>
														{orgIdError}
													</Alert>
												)}
												<Box sx={{ alignSelf: 'center' }}>
													<LoadingButton
														type='submit'
														variant='contained'
														loading={orgIdLoading}
														disabled={
															!isEditingOrgId || salesforceOrgId.length !== 15
														}
													>
														Update Org ID
													</LoadingButton>
												</Box>
											</Stack>
										</form>
										<Divider sx={{ my: 2 }} />
									</Box>
								</Box>
							</Paper>
						</Box>
					)}
				</Box>
			</>
		);
	}
};

export default TenantAdmin;
